import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { hiringChallenges, employerCapabilities } from '../../data/employers';
import { differentiators, engagementModels } from '../../data/company';
import { faqCategories } from '../../data/faqs';
import { images } from '../../data/images';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Button } from '../../components/ui/Button';
import { Accordion } from '../../components/ui/Accordion';
import { Reveal } from '../../components/ui/Reveal';
import { ProcessTimeline } from '../../components/process/ProcessTimeline';
import { EmployerInquiryForm } from '../../components/forms/EmployerInquiryForm';
export function Employers() {
  usePageMeta('Employers — Hire Talent', 'Partner with Corventa to appoint exceptional executives and specialists through research-led, confidential search.');
  return <>
      <PageHero crumbs={[{
      label: 'Employers'
    }]} kicker="For employers" title="Build Your Leadership Team." intro="From board appointments to scarce specialist expertise, we help organisations find, assess and secure the people their strategy depends on." actions={<>
            <Button to="#inquiry" size="lg" arrow data-id="element-1032" data-mp="279yh2yw792up">
              Discuss Your Hiring Needs
            </Button>
            <Button to="/services" size="lg" variant="outlineLight" data-id="element-1033" data-mp="2l14nzg1t62rm">
              Explore services
            </Button>
          </>} aside={<img src={images.executiveWindow} alt="A senior executive looking out over a city" className="aspect-[4/5] max-h-[460px] w-full rounded-xl object-cover" data-id="element-1034" data-mp="2v1nc4n0eo5fc" />} data-id="element-1031" data-mp="22bomoqiau6a5" />

      <section className="bg-ivory py-24" data-id="element-1035" data-mp="2q07p4peo3oub">
        <div className="container-page" data-id="element-1036" data-mp="10o5newo9l2iq">
          <SectionHeading kicker="Challenges and solutions" title="The hiring problems we solve." data-id="element-1037" data-mp="10p7178wo0345" />
          <div className="mt-12 overflow-hidden rounded-xl border border-line bg-white" data-id="element-1038" data-mp="vq47gkm4xjbh">
            <div className="hidden grid-cols-2 border-b border-line bg-ivory-200 px-8 py-3 text-sm font-medium text-ink md:grid" data-id="element-1039" data-mp="2vs3r2jk45co7">
              <span data-id="element-1040" data-mp="34r77tgvt5fzj">The challenge</span>
              <span data-id="element-1041" data-mp="34q5vl9322ng8">How Corventa responds</span>
            </div>
            <ul className="divide-y divide-line" data-id="element-1042" data-mp="39psbxre6kjt9">
              {hiringChallenges.map(h => <li key={h.challenge} className="grid gap-2 px-6 py-5 md:grid-cols-2 md:gap-8 md:px-8" data-id="element-1043" data-mp="3epd67oieo3zn">
                  <p className="font-display text-xl font-semibold text-ink" data-id="element-1044" data-mp="3jnohm3ocmmjz">{h.challenge}</p>
                  <p className="flex gap-3 leading-relaxed text-muted" data-id="element-1045" data-mp="3of6lp0lo5396">
                    <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" data-id="element-1046" data-mp="3ohakx58mhe34" />
                    {h.response}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-1047" data-mp="6ad14nxza672">
        <div className="container-page grid gap-14 lg:grid-cols-12 lg:items-center" data-id="element-1048" data-mp="66yxzdhhpsyk">
          <div className="lg:col-span-6" data-id="element-1049" data-mp="gejmphjdqtzd">
            <img src={images.leadersMeeting} alt="Two executives in discussion" loading="lazy" className="aspect-[4/3] w-full rounded-xl object-cover" data-id="element-1050" data-mp="bdm9zkh52s59" />
          </div>
          <div className="space-y-8 lg:col-span-6" data-id="element-1051" data-mp="6gvt4uxifsis">
            <SectionHeading kicker="Capabilities" title="Search capability, end to end." data-id="element-1052" data-mp="2eio68ql7yn2" />
            {employerCapabilities.map(c => <Reveal key={c.title} className="border-t border-line pt-6" data-id="element-1053" data-mp="z5uavqxq0snk">
                <h3 className="font-display text-2xl font-semibold text-ink" data-id="element-1054" data-mp="u56mze4sgrxk">{c.title}</h3>
                <p className="mt-2 leading-relaxed text-muted" data-id="element-1055" data-mp="pclv4i5g0owo">{c.text}</p>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-midnight-800 py-24" data-id="element-1056" data-mp="1htzcosuti3jq">
        <div className="container-page" data-id="element-1057" data-mp="1hvsiy71iqlr7">
          <SectionHeading tone="dark" kicker="Search engagement process" title="How a search with Corventa unfolds." data-id="element-1058" data-mp="1d3j01aytmlyf" />
          <div className="mt-14" data-id="element-1059" data-mp="xkyfonh697zl">
            <ProcessTimeline variant="compact" tone="dark" data-id="element-1060" data-mp="xjwsxlqsmx21" />
          </div>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-1061" data-mp="12m5kqerxjt2k">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-1062" data-mp="ol3108oequut">
          <div className="lg:col-span-4" data-id="element-1063" data-mp="omw9zumhtr5z">
            <SectionHeading kicker="Partnership" title="The benefits of partnering with Corventa." data-id="element-1064" data-mp="slvc77gub9tq" />
          </div>
          <div className="grid gap-10 sm:grid-cols-2 lg:col-span-8" data-id="element-1065" data-mp="1gd0pbevebo0c">
            {differentiators.map(d => <div key={d.title} data-id="element-1066" data-mp="1lakdp7lt5h60">
                <span className="block h-2.5 w-2.5 rotate-45 bg-gold" aria-hidden="true" data-id="element-1067" data-mp="1lcwsxd06ldfd" />
                <h3 className="mt-4 font-display text-2xl font-semibold text-ink" data-id="element-1068" data-mp="17hkx12aahgwv">{d.title}</h3>
                <p className="mt-2 leading-relaxed text-muted" data-id="element-1069" data-mp="1bj676fvfvh3d">{d.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-1070" data-mp="241trvu6iwu3x">
        <div className="container-page" data-id="element-1071" data-mp="243ocf7dvuk7h">
          <SectionHeading kicker="Engagement models" title="Choose the model that fits the role." data-id="element-1072" data-mp="3orgvm8fckm8g" />
          <div className="mt-12 overflow-x-auto rounded-xl border border-line bg-white" data-id="element-1073" data-mp="3jtfcxolu2oxi">
            <table className="w-full min-w-[640px] text-left" data-id="element-1074" data-mp="2c4jg09e5rvz">
              <thead className="border-b border-line bg-ivory-200 text-sm text-ink" data-id="element-1075" data-mp="2egzu8zi3u6x">
                <tr data-id="element-1076" data-mp="3tjh4iqdeaph6">
                  <th scope="col" className="px-6 py-3 font-medium" data-id="element-1077" data-mp="3tl1oxn5zws1b">Model</th>
                  <th scope="col" className="px-6 py-3 font-medium" data-id="element-1078" data-mp="35td2uojrozst">What it involves</th>
                  <th scope="col" className="px-6 py-3 font-medium" data-id="element-1079" data-mp="30vky2ty0xj77">Best for</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-1080" data-mp="3fp7pdur43f0v">
                {engagementModels.map(m => <tr key={m.title} data-id="element-1081" data-mp="3aukamnlwtyzh">
                    <th scope="row" className="px-6 py-5 font-display text-xl font-semibold text-ink" data-id="element-1082" data-mp="v0a82y3kg113">{m.title}</th>
                    <td className="px-6 py-5 leading-relaxed text-muted" data-id="element-1083" data-mp="q0xcnoymplmw">{m.text}</td>
                    <td className="px-6 py-5 text-sm text-ink" data-id="element-1084" data-mp="x5ycvj5rjdv4">{m.bestFor}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted" data-id="element-1085" data-mp="173macgur6zgz">Fees and terms are agreed in writing before any engagement begins.</p>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-1086" data-mp="1c14ja78rteoz">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-1087" data-mp="eep63hndn378">
          <div className="lg:col-span-4" data-id="element-1088" data-mp="egib6sicdfd4">
            <SectionHeading kicker="Employer FAQs" title="Questions from hiring leaders." data-id="element-1089" data-mp="jdigv2ba4g3p" />
          </div>
          <div className="lg:col-span-8" data-id="element-1090" data-mp="ocbls6ci2ys9">
            <Accordion items={[...faqCategories[0].items, ...faqCategories[2].items.slice(1)]} defaultOpen={0} data-id="element-1091" data-mp="obk1hrocsnha" />
          </div>
        </div>
      </section>

      <section id="inquiry" className="bg-ivory py-24" data-id="element-1092" data-mp="3ru3i1wxo64w9">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-1093" data-mp="3rrhdm662s5i9">
          <div className="lg:col-span-4" data-id="element-1094" data-mp="lzemxp2x62q">
            <SectionHeading kicker="Client inquiry" title="Discuss your hiring needs." data-id="element-1095" data-mp="2dxk2w95mv2vz" />
            <p className="mt-5 leading-relaxed text-muted" data-id="element-1096" data-mp="2dv7p886wreoq">
              Tell us about the role. A consultant will arrange an initial conversation to understand your requirements and recommend an approach.
            </p>
          </div>
          <div className="rounded-xl border border-line bg-white p-6 shadow-soft md:p-10 lg:col-span-8" data-id="element-1097" data-mp="1zyken2m18q0c">
            <EmployerInquiryForm data-id="element-1098" data-mp="1uyy4x35exrwa" />
          </div>
        </div>
      </section>
    </>;
}